/**
 * fits-these-machines.js
 *
 * Powers the expandable "Fits these machines" diagram viewer on product pages.
 *
 * What it does:
 *  - Reads diagram data from card data attributes (set by Liquid)
 *  - When a card is clicked, expands an inline diagram viewer below the cards
 *  - Renders clickable pins over the diagram image
 *  - Fetches part product data from Shopify AJAX API
 *  - Highlights the pin/part matching the current product
 *  - Supports search, ATC from parts list
 */

(function () {
  'use strict';

  // ── Helpers ──────────────────────────────────────────────
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (parseInt(cents, 10) / 100).toFixed(2);
    return (format || '${{ amount }}').replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  // ── Product cache ─────────────────────────────────────────
  const cache = new Map();

  async function fetchProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const data = await res.json();
      cache.set(handle, data);
      return data;
    } catch (e) {
      return null;
    }
  }

  // ── State ─────────────────────────────────────────────────
  let activeCardIndex = null;
  let activePinId     = null;
  let currentPins     = [];
  let partsData       = {};
  let searchQuery     = '';
  let imgRect         = null;

  // ── Load thumbnails for all cards on page load ───────────
  async function loadCardThumbnails() {
    const cards = $$('.ftm-card');
    for (const card of cards) {
      const handle  = card.dataset.productHandle;
      const index   = card.dataset.index;
      const thumbEl = $(`#ftm-thumb-${index}`);
      if (!handle || !thumbEl) continue;
      const product = await fetchProduct(handle);
      if (product?.images?.[0]) {
        const img = document.createElement('img');
        img.src       = product.images[0].replace(/(\.\w+)(\?|$)/, '_80x80$1$2');
        img.className = 'ftm-card__part-thumb';
        img.alt       = product.title;
        thumbEl.parentNode.replaceChild(img, thumbEl);
      }
    }
  }

  // ── Card toggle ───────────────────────────────────────────
  window.ftmToggleCard = function (index) {
    // On mobile, go directly to the diagram page with pin pre-highlighted
    if (window.innerWidth <= 768) {
      const card = document.getElementById('ftm-card-' + index);
      if (card) {
        const pinLabel = card.dataset.pinLabel;
        const url = '/pages/' + card.dataset.handle + (pinLabel ? '?pin=' + encodeURIComponent(pinLabel) : '');
        window.location.href = url;
      }
      return;
    }
    if (activeCardIndex === index) {
      ftmClose();
      return;
    }
    openCard(index);
  };

  window.ftmClose = function () {
    const expand = $('#ftm-expand');
    if (!expand) return;
    expand.hidden = true;
    // Reset all cards
    $$('.ftm-card').forEach(c => c.classList.remove('ftm-card--active'));
    $$('.ftm-card__btn').forEach(b => {
      b.textContent = 'View interactive diagram';
      b.classList.remove('ftm-card__btn--close');
    });
    activeCardIndex = null;
    activePinId     = null;
    currentPins     = [];
    partsData       = {};
  };

  async function openCard(index) {
    const card = $(`#ftm-card-${index}`);
    if (!card) return;

    // Flip button state on all cards
    $$('.ftm-card').forEach((c, i) => c.classList.toggle('ftm-card--active', i === index));
    $$('.ftm-card__btn').forEach((b, i) => {
      b.textContent     = i === index ? 'Close diagram' : 'View diagram';
      b.classList.toggle('ftm-card__btn--close', i === index);
    });

    activeCardIndex = index;

    // Read data from card attributes
    const title     = card.dataset.title;
    const imageSrc  = card.dataset.image;
    const pinLabel  = card.dataset.pinLabel;
    let   pins      = [];

    try {
      pins = JSON.parse(card.dataset.pins.replace(/&quot;/g, '"').replace(/&#39;/g, "'"));
    } catch (e) {
      console.warn('[FTM] Could not parse pins JSON');
    }

    currentPins = pins;

    // Show expand panel
    const expand = $('#ftm-expand');
    expand.hidden = false;

    // Set title
    $('#ftm-diagram-title').textContent = title;

    // Set image
    const diagImg = $('#ftm-diagram-img');
    diagImg.src = imageSrc;
    diagImg.alt = title;
    $('#ftm-pins-layer').innerHTML = '';

    const doRender = () => {
      // Recalculate after a frame so the panel has rendered dimensions
      requestAnimationFrame(() => {
        calcImgRect();
        renderPins();
        const currentPin = pins.find(p => p.product_handle === window.FTM_CURRENT_HANDLE);
        if (currentPin) selectPin(currentPin.id);
        // Re-render once more after scroll settles
        setTimeout(() => { calcImgRect(); renderPins(); }, 300);
      });
    };

    diagImg.onload = doRender;

    if (diagImg.complete && diagImg.naturalWidth > 0) {
      doRender();
    } else if (diagImg.complete && diagImg.naturalWidth === 0) {
      // Poll until natural dimensions arrive
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (diagImg.naturalWidth > 0 || attempts > 40) {
          clearInterval(poll);
          doRender();
        }
      }, 50);
    }

    // Scroll into view
    setTimeout(() => expand.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60);

    // Load parts
    partsData = {};
    $('#ftm-parts-list').innerHTML = '<li class="ftm-parts-loading">Loading parts...</li>';
    $('#ftm-parts-count').textContent = '';

    const handles = [...new Set(pins.map(p => p.product_handle).filter(Boolean))];
    await Promise.all(handles.map(async h => {
      const product = await fetchProduct(h);
      if (product) partsData[h] = product;
    }));

    renderPartsList();
    bindSearch();

    // Resize observer for pin repositioning
    const ro = new ResizeObserver(debounce(() => { calcImgRect(); renderPins(); }, 100));
    ro.observe(diagImg);
  }

  // ── Image rect ────────────────────────────────────────────
  function calcImgRect() {
    const img  = $('#ftm-diagram-img');
    const wrap = $('#ftm-diagram-wrap');
    if (!img || !wrap) return;
    const wr = wrap.getBoundingClientRect();
    if (!wr.width || !wr.height) return;
    const natW = img.naturalWidth  || img.width  || wr.width;
    const natH = img.naturalHeight || img.height || wr.height;
    // Image uses object-fit:contain filling the full wrap — no manual padding
    const scale = Math.min(wr.width / natW, wr.height / natH);
    const rw    = natW * scale;
    const rh    = natH * scale;
    imgRect = {
      x: (wr.width  - rw) / 2,
      y: (wr.height - rh) / 2,
      w: rw,
      h: rh
    };
  }

  // ── Pins ──────────────────────────────────────────────────
  function renderPins() {
    const layer = $('#ftm-pins-layer');
    if (!layer || !imgRect) return;
    layer.innerHTML = '';

    currentPins.forEach(pin => {
      const x    = imgRect.x + (pin.x / 100) * imgRect.w;
      const y    = imgRect.y + (pin.y / 100) * imgRect.h;
      const size = 26;
      const half = size / 2;
      const isActive = pin.id === activePinId;

      const btn = document.createElement('button');
      btn.type      = 'button';
      btn.className = 'ftm-pin' + (isActive ? ' ftm-pin--active' : '');
      btn.setAttribute('aria-label', 'Part ' + pin.label);
      btn.style.cssText = `left:${x - half}px;top:${y - half}px;width:${size}px;height:${size}px;`;
      btn.textContent   = pin.label;
      btn.addEventListener('click', () => selectPin(pin.id));
      layer.appendChild(btn);
    });
  }

  function selectPin(id) {
    activePinId = id;
    renderPins();
    renderPartsList();
    // Scroll active part into view
    setTimeout(() => {
      const row = $(`.ftm-part-row[data-pin-id="${id}"]`, $('#ftm-parts-list'));
      row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  // ── Parts list ────────────────────────────────────────────
  function renderPartsList() {
    const list  = $('#ftm-parts-list');
    const count = $('#ftm-parts-count');
    if (!list) return;

    const query    = searchQuery.toLowerCase();
    const filtered = currentPins.filter(pin => {
      if (!query) return true;
      const product = partsData[pin.product_handle];
      const name    = (product?.title || '').toLowerCase();
      const sku     = (product?.variants?.[0]?.sku || '').toLowerCase();
      const label   = String(pin.label).toLowerCase();
      return name.includes(query) || sku.includes(query) || label.includes(query);
    });

    if (count) count.textContent = filtered.length + ' of ' + currentPins.length + ' parts';

    if (!filtered.length) {
      list.innerHTML = '<li class="ftm-parts-empty">No parts match your search.</li>';
      return;
    }

    list.innerHTML = filtered.map(pin => {
      const product  = partsData[pin.product_handle];
      const isActive = pin.id === activePinId;
      const isCurrent = pin.product_handle === window.FTM_CURRENT_HANDLE;
      const price    = product ? formatMoney(product.price_min, window.FTM_MONEY_FORMAT) : '';
      const sku      = product?.variants?.[0]?.sku || '';
      const inStock  = product ? product.available : false;
      const thumbSrc = product?.images?.[0]
        ? product.images[0].replace(/(\.\w+)(\?|$)/, '_80x80$1$2')
        : null;

      return `
        <li
          class="ftm-part-row${isActive ? ' ftm-part-row--active' : ''}${isCurrent ? ' ftm-part-row--current' : ''}"
          data-pin-id="${pin.id}"
          role="button"
          tabindex="0"
        >
          <span class="ftm-part-row__num${isActive ? ' ftm-part-row__num--active' : ''}">${pin.label}</span>
          ${thumbSrc
            ? `<img src="${thumbSrc}" class="ftm-part-row__thumb" alt="" onerror="this.style.display='none'">`
            : `<span class="ftm-part-row__thumb ftm-part-row__thumb--empty"></span>`
          }
          <span class="ftm-part-row__info">
            <span class="ftm-part-row__name">${product ? product.title : pin.product_handle}</span>
            ${sku ? `<span class="ftm-part-row__sku">${sku}</span>` : ''}
            ${isCurrent ? `<span class="ftm-part-row__current">Viewing this product</span>` : ''}
            ${!inStock && product ? `<span class="ftm-part-row__oos">Out of stock</span>` : ''}
          </span>
          <span class="ftm-part-row__right">
            ${price ? `<span class="ftm-part-row__price">${price}</span>` : ''}
            ${product && inStock
              ? `<button class="ftm-part-row__atc" data-variant="${product.variants[0]?.id}" onclick="ftmAddToCart(this, event)">Add</button>`
              : product && !inStock
              ? `<span class="ftm-part-row__oos-btn">Out of stock</span>`
              : ''
            }
          ${product ? `<a class="ftm-part-row__link" href="/products/${product.handle}">View product</a>` : ''}
          </span>
        </li>`;
    }).join('');

    // Bind row clicks
    $$('.ftm-part-row', list).forEach(row => {
      row.addEventListener('click', e => {
        if (e.target.closest('.ftm-part-row__atc')) return;
        selectPin(parseInt(row.dataset.pinId, 10));
      });
      row.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectPin(parseInt(row.dataset.pinId, 10)); }
      });
    });
  }

  // ── Search ────────────────────────────────────────────────
  function bindSearch() {
    const input = $('#ftm-search');
    if (!input) return;
    // Remove old listener by replacing element
    const newInput = input.cloneNode(true);
    input.parentNode.replaceChild(newInput, input);
    newInput.addEventListener('input', debounce(e => {
      searchQuery = e.target.value;
      renderPartsList();
    }, 200));
  }

  // ── Add to cart ───────────────────────────────────────────
  window.ftmAddToCart = async function (btn, e) {
    e.stopPropagation();
    const variantId = parseInt(btn.dataset.variant, 10);
    if (!variantId) return;
    const orig     = btn.textContent;
    btn.textContent = 'Adding...';
    btn.disabled    = true;
    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('failed');
      const cartRes  = await fetch('/cart.js');
      const cartData = await cartRes.json();
      $$('[data-cart-count],[data-cart-items-count]').forEach(el => el.textContent = cartData.item_count);
      btn.textContent = 'Added!';
      btn.style.background = '#A83800';
      setTimeout(() => {
        btn.textContent      = orig;
        btn.style.background = '';
        btn.disabled         = false;
      }, 2000);
    } catch (err) {
      btn.textContent = 'Error';
      btn.disabled    = false;
      setTimeout(() => { btn.textContent = orig; }, 1500);
    }
  };

  // Load thumbnails on page ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadCardThumbnails);
  } else {
    loadCardThumbnails();
  }

})();